import { apiBuilder, prefixApi } from "@zodios/core";
import { z } from "zod";
import { columnFilterSchema } from "../../../common/columnFilterSchema";
import { cursorSchema } from "../../../common/cursorSchema";

const searchTermPerformanceStatsSchema = z.object({
  clicks: z.number(),
  clicks_prev: z.number().nullable().optional(),
  clicks_delta: z.number().nullable().optional(),
  clicks_percent: z.number().nullable().optional(),

  impressions: z.number(),
  impressions_prev: z.number().nullable().optional(),
  impressions_delta: z.number().nullable().optional(),
  impressions_percent: z.number().nullable().optional(),

  click_thru_rate: z.number(),
  click_thru_rate_prev: z.number().nullable().optional(),
  click_thru_rate_delta: z.number().nullable().optional(),
  click_thru_rate_percent: z.number().nullable().optional(),

  ad_sales: z.number(),
  ad_sales_prev: z.number().nullable().optional(),
  ad_sales_delta: z.number().nullable().optional(),
  ad_sales_percent: z.number().nullable().optional(),

  cost: z.number(),
  cost_prev: z.number().nullable().optional(),
  cost_delta: z.number().nullable().optional(),
  cost_percent: z.number().nullable().optional(),

  cost_per_click: z.number(),
  cost_per_click_prev: z.number().nullable().optional(),
  cost_per_click_delta: z.number().nullable().optional(),
  cost_per_click_percent: z.number().nullable().optional(),

  acos: z.number(),
  acos_prev: z.number().nullable().optional(),
  acos_delta: z.number().nullable().optional(),
  acos_percent: z.number().nullable().optional(),

  roas: z.number(),
  roas_prev: z.number().nullable().optional(),
  roas_delta: z.number().nullable().optional(),
  roas_percent: z.number().nullable().optional(),

  conversion: z.number().nullable().optional(),
  conversion_prev: z.number().nullable().optional(),
  conversion_delta: z.number().nullable().optional(),
  conversion_percent: z.number().nullable().optional(),

  conversions_14d: z.number(),
  conversions_14d_prev: z.number().nullable().optional(),
  conversions_14d_delta: z.number().nullable().optional(),
  conversions_14d_percent: z.number().nullable().optional(),

  conv_rate: z.number(),
  conv_rate_prev: z.number().nullable().optional(),
  conv_rate_delta: z.number().nullable().optional(),
  conv_rate_percent: z.number().nullable().optional(),

  attributed_conversions_7d: z.number(),
  attributed_conversions_7d_prev: z.number().nullable().optional(),
  attributed_conversions_7d_delta: z.number().nullable().optional(),
  attributed_conversions_7d_percent: z.number().nullable().optional(),

  attributed_conversions_7d_same_sku: z.number(),
  attributed_conversions_7d_same_sku_prev: z.number().nullable().optional(),
  attributed_conversions_7d_same_sku_delta: z.number().nullable().optional(),
  attributed_conversions_7d_same_sku_percent: z.number().nullable().optional(),

  attributed_units_ordered_7d: z.number(),
  attributed_units_ordered_7d_prev: z.number().nullable().optional(),
  attributed_units_ordered_7d_delta: z.number().nullable().optional(),
  attributed_units_ordered_7d_percent: z.number().nullable().optional(),

  attributed_sales_7d: z.number(),
  attributed_sales_7d_prev: z.number().nullable().optional(),
  attributed_sales_7d_delta: z.number().nullable().optional(),
  attributed_sales_7d_percent: z.number().nullable().optional(),

  attributed_sales_7d_same_sku: z.number(),
  attributed_sales_7d_same_sku_prev: z.number().nullable().optional(),
  attributed_sales_7d_same_sku_delta: z.number().nullable().optional(),
  attributed_sales_7d_same_sku_percent: z.number().nullable().optional(),

  attributed_units_ordered_7d_same_sku: z.number(),
  attributed_units_ordered_7d_same_sku_prev: z.number().nullable().optional(),
  attributed_units_ordered_7d_same_sku_delta: z.number().nullable().optional(),
  attributed_units_ordered_7d_same_sku_percent: z.number().nullable().optional(),

  // SB v3 metrics - likely only needed in ad details STR table
  add_to_list: z.number().nullable().optional(),
  add_to_list_prev: z.number().nullable().optional(),
  add_to_list_delta: z.number().nullable().optional(),
  add_to_list_percent: z.number().nullable().optional(),

  add_to_list_from_clicks: z.number().nullable().optional(),
  add_to_list_from_clicks_prev: z.number().nullable().optional(),
  add_to_list_from_clicks_delta: z.number().nullable().optional(),
  add_to_list_from_clicks_percent: z.number().nullable().optional(),

  viewability_rate: z.number(),
  viewability_rate_prev: z.number().nullable().optional(),
  viewability_rate_delta: z.number().nullable().optional(),
  viewability_rate_percent: z.number().nullable().optional(),

  viewable_impressions: z.number(),
  viewable_impressions_prev: z.number().nullable().optional(),
  viewable_impressions_delta: z.number().nullable().optional(),
  viewable_impressions_percent: z.number().nullable().optional(),

  view_click_through_rate: z.number(),
  view_click_through_rate_prev: z.number().nullable().optional(),
  view_click_through_rate_delta: z.number().nullable().optional(),
  view_click_through_rate_percent: z.number().nullable().optional(),

  sales: z.number().nullable().optional(),
  sales_prev: z.number().nullable().optional(),
  sales_delta: z.number().nullable().optional(),
  sales_percent: z.number().nullable().optional(),

  units_ordered_14d: z.number().nullable().optional(),
  units_ordered_14d_prev: z.number().nullable().optional(),
  units_ordered_14d_delta: z.number().nullable().optional(),
  units_ordered_14d_percent: z.number().nullable().optional(),
});

export const searchTermPerformanceReportApiDefinitions = prefixApi(
  "/api",
  apiBuilder({
    alias: "getV4SearchTermPerformanceByQuery",
    method: "get",
    path: "/v4/query/?table=str",
    parameters: [
      {
        name: "marketplace",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "grouping",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "start_date",
        type: "Query",
        schema: z.string().date(),
      },
      {
        name: "end_date",
        type: "Query",
        schema: z.string().date(),
      },
      {
        name: "start_date_previous",
        type: "Query",
        schema: z.string().date(),
      },
      {
        name: "end_date_previous",
        type: "Query",
        schema: z.string().date(),
      },
      {
        name: "filters",
        type: "Query",
        schema: columnFilterSchema,
      },
      {
        name: "cursor",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().array().optional(),
      },
    ],
    status: 200,
    response: z.object({
      data: z.array(
        z
          .object({
            keyword_text: z.string(),
            query: z.string(),
            match_type: z.string(),
            search_term_type: z.string(),
            marketplace: z.string(),
            campaign_name: z.string().optional(),
            campaign_id: z.number().optional(),
            ad_name: z.string().optional(),
            ad_goal: z.number().nullable().optional(),
            ad_group_name: z.string().optional(),
            ad_group_id: z.number().optional(),
            adv_keyword_ids: z.array(z.number()),
            adv_keyword_ids_count: z.number(),
            target_name: z.string().optional(),
            adv_target_ids: z.array(z.number()),
            adv_target_ids_count: z.number(),
            keyword_bid: z.number().optional(),
            ad_id: z.number(),
            category_id: z.number(),
            managed: z.literal("Yes").or(z.literal("No")).optional(),
          })
          .merge(searchTermPerformanceStatsSchema),
      ),
      cursor: cursorSchema,
      count: z.number(),
    }),
  })
    .addEndpoint({
      alias: "getV4SearchTermPerformanceTotals",
      method: "get",
      path: "/v4/query/aggregate/?table=str",
      parameters: [
        {
          name: "marketplace",
          type: "Query",
          schema: z.string(),
        },
        {
          name: "start_date",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "end_date",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "start_date_previous",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "end_date_previous",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "filters",
          type: "Query",
          schema: columnFilterSchema,
        },

        {
          name: "search",
          type: "Query",
          schema: z.string().array().optional(),
        },
      ],
      status: 200,
      response: z.object({
        data: searchTermPerformanceStatsSchema,
      }),
    })
    .addEndpoint({
      alias: "getV4SearchTermPerformanceByDate",
      method: "get",
      path: "/v4/query/?table=str&grouping=start_date&limit=1000000&include_count=false",
      parameters: [
        {
          name: "marketplace",
          type: "Query",
          schema: z.string(),
        },
        {
          name: "start_date",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "end_date",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "start_date_previous",
          type: "Query",
          schema: z.string().date().optional(),
        },
        {
          name: "end_date_previous",
          type: "Query",
          schema: z.string().date().optional(),
        },
        {
          name: "date_group",
          type: "Query",
          schema: z.string(),
        },
        {
          name: "filters",
          type: "Query",
          schema: columnFilterSchema,
        },

        {
          name: "search",
          type: "Query",
          schema: z.string().array().optional(),
        },
      ],
      status: 200,
      response: z.object({
        data: z.array(z.object({ start_date: z.string().date() }).merge(searchTermPerformanceStatsSchema)),
      }),
    })
    .addEndpoint({
      alias: "getV4SearchTermReportExport",
      method: "get",
      path: "/v4/query/?table=str&xls=true",
      parameters: [
        {
          name: "marketplace",
          type: "Query",
          schema: z.string(),
        },
        {
          name: "start_date",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "end_date",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "grouping",
          type: "Query",
          schema: z.string(),
        },
        {
          name: "report_type",
          type: "Query",
          schema: z.string(),
        },
        {
          name: "filters",
          type: "Query",
          schema: columnFilterSchema,
        },
      ],
      status: 200,
      response: z.instanceof(Blob),
    })
    .build(),
);
